import { useMemo } from 'react';
import { buildScheduleRows, formatDateTimeHeader } from '@betreuung/shared';
import { flattenPages, useUpcomingAssignments } from '@/api/queries';
import { useAppData } from '@/app/appData';
import { TabHeader } from '@/app/TabHeader';
import { useIsDesktop } from '@/app/useMediaQuery';
import {
  InfiniteSentinel,
  ListMessage,
  MonthDividerRow,
  SkeletonRows,
} from '@/components/shared/lists';
import { useAssignmentFlows } from '@/features/assignment/useAssignmentFlows';
import { de } from '@/i18n/de';
import { RunningCard } from './RunningCard';
import { ScheduleRowItem } from './ScheduleRowItem';

/**
 * Landing screen: what runs right now, followed by every upcoming assignment in
 * order. Unassigned stretches get a row of their own, so the list reads as one
 * continuous timeline and tapping a gap is the natural way to fill it.
 */
export function CurrentTab() {
  const { persons, now, nowSlot } = useAppData();
  const { openCreate, openEdit } = useAssignmentFlows();
  const isDesktop = useIsDesktop();
  const query = useUpcomingAssignments();

  const rows = useMemo(
    () => buildScheduleRows(flattenPages(query.data?.pages, 'assignments'), nowSlot),
    [query.data, nowSlot],
  );

  // buildScheduleRows always emits the card row first and the list rows after.
  const [card, ...list] = rows;

  return (
    <div className="nav:max-w-[720px]">
      <TabHeader
        title={de.tabs.current}
        subtitle={formatDateTimeHeader(now)}
        showSettings={!isDesktop}
      />

      {query.isPending ? (
        <SkeletonRows count={6} />
      ) : query.isLoadingError ? (
        <ListMessage>{de.errors.loadFailed}</ListMessage>
      ) : (
        <>
          {card?.kind === 'running' || card?.kind === 'unassignedNow' ? (
            <RunningCard row={card} persons={persons} />
          ) : null}

          {list.map((row) => {
            switch (row.kind) {
              case 'monthDivider':
                return (
                  <MonthDividerRow
                    key={`m${row.year}-${row.month}`}
                    year={row.year}
                    month={row.month}
                  />
                );
              case 'assignment':
                return (
                  <ScheduleRowItem
                    key={`a${row.assignment.id}`}
                    startSlot={row.assignment.startSlot}
                    endSlot={row.assignment.endSlot}
                    person={persons.describe(row.assignment.personId)}
                    onClick={() => openEdit(row.assignment)}
                  />
                );
              case 'gap':
                return (
                  <ScheduleRowItem
                    key={`g${row.startSlot}`}
                    startSlot={row.startSlot}
                    endSlot={row.endSlot}
                    person={null}
                    onClick={() => openCreate({ startSlot: row.startSlot, endSlot: row.endSlot })}
                  />
                );
              default:
                return null;
            }
          })}

          {/* A first page holding nothing but the running assignment can still
              have successors, so the empty state waits for the last page. */}
          {list.length === 0 && !query.hasNextPage ? (
            <ListMessage>{de.current.empty}</ListMessage>
          ) : null}

          {query.isFetchingNextPage ? <SkeletonRows count={3} /> : null}
          <InfiniteSentinel
            onReach={() => void query.fetchNextPage()}
            enabled={query.hasNextPage && !query.isFetchingNextPage}
            footer={de.current.loadingMore}
          />
        </>
      )}
    </div>
  );
}
